import { useAuth } from '@/contexts/AuthContext';
import KpiCard from '@/components/dashboard/KpiCard';
import ChartCard from '@/components/dashboard/ChartCard';
import useDashboardV2 from '@/hooks/useDashboardV2';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, Target, Activity, DollarSign, TrendingUp } from 'lucide-react';

/**
 * Dashboard del Vendedor — vista personal cross-vertical.
 *
 * El backend ya filtra por `seller_id` en los endpoints v2; aquí pasamos el
 * ID del usuario logueado como filtro fijo.
 */
export default function VendorDashboard() {
  const { user } = useAuth();
  const filters = { seller_id: user?.id };

  const kpisQ = useDashboardV2({ endpoint: 'kpis', filters });
  const trendsQ = useDashboardV2({ endpoint: 'trends', filters, extra: { months_back: 6 } });

  const core = kpisQ.data?.core || {};
  const monthly = trendsQ.data?.monthly || [];

  return (
    <div className="space-y-6" data-testid="dashboard-vendor">
      <div>
        <h2 className="font-heading text-2xl font-bold text-white">Mi rendimiento</h2>
        <p className="text-muted-foreground text-sm mt-1">Tu desempeño personal en los últimos 6 meses</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Mis Leads" value={kpisQ.loading ? '—' : core.total_leads ?? 0} color="cyan" loading={kpisQ.loading} testid="kpi-my-leads" />
        <KpiCard icon={Target} label="Mi Conversión" value={kpisQ.loading ? '—' : `${core.conversion_rate ?? 0}%`} sub={`${core.won_leads ?? 0} ganados`} color="emerald" loading={kpisQ.loading} testid="kpi-my-conversion" />
        <KpiCard icon={Activity} label="Pipeline" value={kpisQ.loading ? '—' : core.total_opportunities ?? 0} color="violet" loading={kpisQ.loading} testid="kpi-my-pipeline" />
        <KpiCard icon={DollarSign} label="Valor pipeline" value={kpisQ.loading ? '—' : `$${((core.total_pipeline_value ?? 0) / 1000).toFixed(1)}k`} sub="MXN" color="amber" loading={kpisQ.loading} testid="kpi-my-value" />
      </div>

      <ChartCard title="Mi evolución mensual" icon={TrendingUp} loading={trendsQ.loading} empty={!monthly.length} testid="chart-vendor-trend">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <Tooltip contentStyle={{ background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }} />
            <Bar dataKey="leads" fill="#1B9CEA" radius={[4, 4, 0, 0]} name="Leads" />
            <Bar dataKey="ganados" fill="#10B981" radius={[4, 4, 0, 0]} name="Ganados" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
